// src/context/ThemeContext.js
import { createContext, useState, useEffect, useCallback } from "react";

export const ThemeContext = createContext();

// ── Color utilities ────────────────────────────────────────────
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}
function toHex(r, g, b) {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`;
}
function mixHex(a, b, t = 0.5) {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  return toHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
function darken(hex, amt = 0.12) {
  const [r, g, b] = hexToRgb(hex);
  return toHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}
function lighten(hex, amt = 0.08) {
  const [r, g, b] = hexToRgb(hex);
  return toHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
function contrastText(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? "#111111" : "#ffffff";
}

export function buildCustomVars(c) {
  return {
    "--bg":           c.bg,
    "--card":         c.card,
    "--text":         c.text,
    "--accent":       c.accent,
    "--accent-text":  contrastText(c.accent),
    "--accent-hover": darken(c.accent, 0.14),
    "--border":       mixHex(c.card, c.accent, 0.25),
    "--muted":        mixHex(c.bg, c.card, 0.55),
    "--input":        lighten(c.card, 0.06),
  };
}

export const DEFAULT_CUSTOM_COLORS = {
  bg:     "#0f172a",
  card:   "#1e293b",
  accent: "#6366f1",
  text:   "#f1f5f9",
};

// ── Built-in themes ────────────────────────────────────────────
const THEME_STYLES = {
  light: {
    "--bg":           "#faeedd",
    "--card":         "#fdf4e3",
    "--text":         "#1f1508",
    "--accent":       "#9a6e00",
    "--accent-text":  "#ffffff",
    "--accent-hover": "#7d5a00",
    "--border":       "#d4ab60",
    "--muted":        "#f0dca8",
    "--input":        "#fffcf5",
  },
  dark: {
    "--bg":           "#1c1c1c",
    "--card":         "#262626",
    "--text":         "#f5f5f5",
    "--accent":       "#e5b94b",
    "--accent-text":  "#1c1c1c",
    "--accent-hover": "#d4a835",
    "--border":       "#3a3a3a",
    "--muted":        "#2f2f2f",
    "--input":        "#333333",
  },
};

// ── Provider ───────────────────────────────────────────────────
export const ThemeProvider = ({ children }) => {
  const [theme, _setTheme] = useState(() => {
    const s = localStorage.getItem("theme");
    return ["light", "dark", "custom"].includes(s) ? s : "light";
  });

  const [customColors, _setCustomColors] = useState(() => {
    try {
      const s = localStorage.getItem("theme_custom_colors");
      return s ? { ...DEFAULT_CUSTOM_COLORS, ...JSON.parse(s) } : { ...DEFAULT_CUSTOM_COLORS };
    } catch {
      return { ...DEFAULT_CUSTOM_COLORS };
    }
  });

  const setTheme = useCallback((name) => {
    _setTheme(name);
    localStorage.setItem("theme", name);
  }, []);

  const setCustomColors = useCallback((colors) => {
    _setCustomColors(colors);
    localStorage.setItem("theme_custom_colors", JSON.stringify(colors));
    _setTheme("custom");
    localStorage.setItem("theme", "custom");
  }, []);

  // Keep backward-compat toggleTheme for components that receive it as a prop
  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  useEffect(() => {
    const vars = theme === "custom"
      ? buildCustomVars(customColors)
      : THEME_STYLES[theme] || THEME_STYLES.light;

    Object.entries(vars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
    document.documentElement.classList.remove("light", "dark", "custom");
    document.documentElement.classList.add(theme);
  }, [theme, customColors]);

  const colors = theme === "custom"
    ? buildCustomVars(customColors)
    : THEME_STYLES[theme] || THEME_STYLES.light;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, customColors, setCustomColors, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};
